import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment-timezone";
import { Row, Col, Select, Input, DatePicker, Radio, Divider } from "antd";

import { useCurrencyContext } from "../../../../hooks/useCurrencyContext";
import { Creators as FinancialIssueActions } from "../../../../store/ducks/financialIssue";
import { Creators as FinancialPaymentMethodActions } from "../../../../store/ducks/financialPaymentMethod";
import { Creators as FinancialTaxableDeductibleActions } from "../../../../store/ducks/financialTaxableDeductible";

import InputCurrency from "../../../../components/utils/inputCurrency";
import SelectCurrency from "../../../../components/utils/selectCurrency";
import { ConversionStatus } from "../../../../components/utils/conversionStatus";
import SearchSupplier from "../../../../components/utils/searchSupplier";
import InfoTooltip from "../../../../components/utils/infoTooltip";

import InstallmentsModal from "../../../financial/tabs/transactions/form/installmentsModal";
import ResultCenterApportionmentModal from "../../../financial/tabs/transactions/form/resultCenterApportionmentModal";

import { findAllFinancialClassesByGroupIdAndFarmId } from "../../../../services/financialClassService";
import { findAll as findAllFinancialProjects } from "../../../../services/financialProjectService";
import { indexAllDropdownBankAccountFarm } from "../../../../services/bankAccountFarmService";

import { currencies } from "../../../../utils/currency";
import { momentTimeZoneZeroHour } from "../../../../utils/dateUtils";

import { FinancialTransactionBlock } from "./styles";

const { Option } = Select;
const { TextArea } = Input;

// Box de despesa financeira vinculada, exibido apenas em movimentações de
// ENTRADA com origem COMPRA OU  em movimentações de SAÍDA com origem VENDA.
const FinancialTransactionBox = ({
  origin,
  type,
  values,
  errors,
  submitCount,
  setFieldValue,
  supplierTextSearch,
  totalItemsCost,
  isEnablePaymentMethod,
  isEnableFinancialIssue,
  isEnableFinancialTaxableDeductible,
  isEnableBankAccountFarm,
}) => {
  const dispatch = useDispatch();
  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);
  const financialIssue = useSelector((state) => state.financialIssue);
  const financialPaymentMethod = useSelector(
    (state) => state.financialPaymentMethod,
  );
  const financialTaxableDeductible = useSelector(
    (state) => state.financialTaxableDeductible,
  );

  const {
    defaultCurrency,
    defaultCurrencyCode,
    farmCurrencies,
    shouldShowConversion,
    convertCurrency,
  } = useCurrencyContext();

  const tf = translation?.financial?.transactions?.form || {};
  const tm =
    translation?.stockItem?.movements?.form?.financialTransaction || {};

  const [filteredListFinancialNature, setFilteredListFinancialNature] =
    useState([]);
  const [isLoadingFinancialNature, setIsLoadingFinancialNature] =
    useState(false);
  const [listFinancialClass, setListFinancialClass] = useState([]);
  const [isLoadingFinancialClass, setIsLoadingFinancialClass] = useState(false);
  const [listFinancialProjects, setListFinancialProjects] = useState([]);
  const [isLoadingFinancialProjects, setIsLoadingFinancialProjects] =
    useState(false);

  const [bankAccountFarmOptions, setBankAccountFarmOptions] = useState([]);
  const [isLoadingBankAccountFarm, setIsLoadingBankAccountFarm] =
    useState(false);
  const [statusCurrencyConversion, setStatusCurrencyConversion] =
    useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(
    currencies.find((c) => c.code === "BRL"),
  );
  const [isInstallmentsModalVisible, setIsInstallmentsModalVisible] =
    useState(false);
  const [isResultCenterModalVisible, setIsResultCenterModalVisible] =
    useState(false);

  const farmCurrenciesCodesFilter =
    farmCurrencies && farmCurrencies.length > 0
      ? farmCurrencies.map((c) => c.code)
      : [];

  useEffect(() => {
    async function getFinancialClassAndNature() {
      setIsLoadingFinancialNature(true);
      setIsLoadingFinancialClass(true);
      try {
        const {
          data: { results: classes },
        } = await findAllFinancialClassesByGroupIdAndFarmId({
          groupId,
          farmId,
        });
        const natures = [
          ...new Map(
            classes
              .map((c) => c.financialNature)
              .map((item) => [item.id, item]),
          ).values(),
        ];
        if (origin === "PURCHASE" && type === "ENTRY") {
          setFilteredListFinancialNature(
            natures.filter((n) => n.type === "expense"),
          );
        } else if (origin === "SALE" && type === "EXIT") {
          setFilteredListFinancialNature(
            natures.filter((n) => n.type === "income"),
          );
        }
        setListFinancialClass(classes);
      } catch (error) {
      } finally {
        setIsLoadingFinancialNature(false);
        setIsLoadingFinancialClass(false);
      }
    }
    async function getFinancialProjects() {
      setIsLoadingFinancialProjects(true);
      try {
        const {
          data: { results: projects },
        } = await findAllFinancialProjects({ groupId, farmId });
        setListFinancialProjects(
          (projects || []).filter((p) => p.status === "Active"),
        );
      } catch (error) {
      } finally {
        setIsLoadingFinancialProjects(false);
      }
    }
    if (groupId && farmId) {
      getFinancialClassAndNature();
      getFinancialProjects();
    }
  }, [groupId, farmId, origin, type]);

  useEffect(() => {
    if (isEnableFinancialIssue) {
      dispatch(
        FinancialIssueActions.getDropdownFinancialIssue(groupId, farmId),
      );
    }
    if (isEnablePaymentMethod) {
      dispatch(
        FinancialPaymentMethodActions.getDropdownFinancialPaymentMethod(
          groupId,
          farmId,
        ),
      );
    }
    if (isEnableFinancialTaxableDeductible) {
      dispatch(
        FinancialTaxableDeductibleActions.getDropdownFinancialTaxableDeductible(
          groupId,
          farmId,
        ),
      );
    }
  }, [
    dispatch,
    groupId,
    farmId,
    isEnableFinancialIssue,
    isEnablePaymentMethod,
    isEnableFinancialTaxableDeductible,
  ]);

  useEffect(() => {
    async function fetchBankAccountFarmOptions() {
      if (isEnableBankAccountFarm && groupId && farmId) {
        setIsLoadingBankAccountFarm(true);
        try {
          const {
            data: { results },
          } = await indexAllDropdownBankAccountFarm({ groupId, farmId });
          setBankAccountFarmOptions(results || []);
        } catch (error) {
          setBankAccountFarmOptions([]);
        } finally {
          setIsLoadingBankAccountFarm(false);
        }
      }
    }
    fetchBankAccountFarmOptions();
  }, [isEnableBankAccountFarm, groupId, farmId]);

  const handleConvertValues = useCallback(async () => {
    if (shouldShowConversion) {
      setStatusCurrencyConversion("loading");
      try {
        const mainValue = values.mainValue ?? 0;
        const fees = values.fees ?? 0;
        const discount = values.discount ?? 0;
        const valueToBeConverted = mainValue + fees - discount;
        const data = await convertCurrency(
          defaultCurrencyCode,
          values.mainValueCurrency,
          valueToBeConverted,
        );
        setFieldValue("value", data.value);
        setStatusCurrencyConversion(data.status);
      } catch (error) {
        setStatusCurrencyConversion("error");
      }
    }
  }, [
    shouldShowConversion,
    values.mainValue,
    values.fees,
    values.discount,
    values.mainValueCurrency,
    defaultCurrencyCode,
    convertCurrency,
    setFieldValue,
  ]);

  useEffect(() => {
    const newMainValue = totalItemsCost || 0;
    if (values.mainValue === newMainValue) return;

    setFieldValue("mainValue", newMainValue);
    setFieldValue("installments", []);
    const newValue =
      newMainValue + ((values.fees || 0) - (values.discount || 0));
    setFieldValue("value", newValue);

    if (shouldShowConversion) {
      setStatusCurrencyConversion("loading");
      convertCurrency(defaultCurrencyCode, values.mainValueCurrency, newValue)
        .then((data) => {
          setFieldValue("value", data.value);
          setStatusCurrencyConversion(data.status);
        })
        .catch(() => setStatusCurrencyConversion("error"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalItemsCost]);

  const handleOpenCloseInstallmentsModal = () =>
    setIsInstallmentsModalVisible((v) => !v);
  const handleOpenCloseResultCenterModal = () =>
    setIsResultCenterModalVisible((v) => !v);

  const handleCancelCustomApportionmentModal = () => {
    handleOpenCloseResultCenterModal();
    setFieldValue("customApportionmentValues", null);
    setFieldValue("resultCenterApportionmentDefault", true);
  };

  const handleSaveCustomApportionmentModal = (data) => {
    handleOpenCloseResultCenterModal();
    setFieldValue("customApportionmentValues", data);
  };

  const hasError = (field) => submitCount > 0 && errors[field];

  return (
    <FinancialTransactionBlock>
      <Divider orientation="left">
        {tm.title || "Despesa Financeira Vinculada"}
      </Divider>

      {submitCount > 0 && errors.installments && values.payment === "part" && (
        <Row type="flex" justify="center" align="middle">
          <label className="label-error">
            {tf.errors?.installmentsNotGeneratedError ||
              "É necessário gerar as parcelas."}
          </label>
        </Row>
      )}

      <Row gutter={16}>
        <Col span={12}>
          <Row>
            <label>{tf.supplierId || "Fornecedor"}</label>
          </Row>
          <SearchSupplier
            disabled
            textSearch={supplierTextSearch}
            setTextSearch={() => {}}
            value={values.supplierId}
            handleChange={() => {}}
          />
        </Col>
        <Col span={12}>
          <Row>
            <label>{tf.documentNumber || "Nº Doc. Fiscal"}</label>
          </Row>
          <Input disabled value={values.documentNumber || ""} />
        </Col>
      </Row>

      <Row gutter={16}>
        {/* Valores */}
        <Col span={8}>
          {shouldShowConversion && (
            <>
              <Row>
                <label>{tf.mainValueCurrency || "Moeda"}</label>
              </Row>
              <Row>
                <SelectCurrency
                  value={values.mainValueCurrency}
                  setValue={(code) => {
                    setFieldValue("mainValueCurrency", code);
                    setSelectedCurrency(
                      currencies.find((c) => c.code === code),
                    );
                  }}
                  filterOptions={farmCurrenciesCodesFilter}
                  onBlur={handleConvertValues}
                />
              </Row>
            </>
          )}
          <Row>
            <label className={hasError("mainValue") ? "label-error" : ""}>
              {tf.mainValue || "Valor Principal"}*
              <InfoTooltip
                title={
                  tm.mainValueHelp ||
                  "Valor calculado automaticamente com base no custo total dos itens desta movimentação (quantidade x custo unitário)."
                }
              />
            </label>
          </Row>
          <Row>
            <InputCurrency
              disabled
              value={values.mainValue != null ? values.mainValue : ""}
              symbol={selectedCurrency?.symbol}
              decimalScale={selectedCurrency?.decimal_digits}
              setValue={() => {}}
            />
          </Row>
          <Row>
            <label>{tf.fees || "Valor Juros/Multas"}</label>
          </Row>
          <Row>
            <InputCurrency
              value={values.fees != null ? values.fees : ""}
              symbol={selectedCurrency?.symbol}
              decimalScale={selectedCurrency?.decimal_digits}
              setValue={(floatValue) => {
                setFieldValue(
                  "fees",
                  floatValue != null ? Math.min(floatValue, 100000000) : null,
                );
                const newValue =
                  (values.mainValue || 0) +
                  ((floatValue || 0) - (values.discount || 0));
                setFieldValue("value", newValue);
                setFieldValue("installments", []);
              }}
              onBlur={handleConvertValues}
            />
          </Row>
          <Row>
            <label>{tf.discount || "Descontos"}</label>
          </Row>
          <Row>
            <InputCurrency
              value={values.discount != null ? values.discount : ""}
              symbol={selectedCurrency?.symbol}
              decimalScale={selectedCurrency?.decimal_digits}
              setValue={(floatValue) => {
                setFieldValue(
                  "discount",
                  floatValue != null ? Math.min(floatValue, 100000000) : null,
                );
                const newValue =
                  (values.mainValue || 0) +
                  ((values.fees || 0) - (floatValue || 0));
                setFieldValue("value", newValue);
                setFieldValue("installments", []);
              }}
              onBlur={handleConvertValues}
            />
          </Row>
          <Row>
            <label className={hasError("value") ? "label-error" : ""}>
              {tf.value || "Valor da transação"}*
            </label>
          </Row>
          <Row type="flex" gutter={8} align="middle">
            <Col>
              <InputCurrency
                disabled={!shouldShowConversion}
                value={values.value != null ? values.value : ""}
                setValue={(floatValue) => setFieldValue("value", floatValue)}
              />
            </Col>
            <Col>
              <ConversionStatus status={statusCurrencyConversion} />
            </Col>
          </Row>
        </Col>

        {/* Pagamento / Ação */}
        <Col span={8}>
          <Row>
            <label
              className={
                submitCount > 0 && errors.installments ? "label-error" : ""
              }
            >
              {tf.payment || "Pagamento"}
            </label>
          </Row>
          <Row>
            <Radio.Group
              style={{ display: "flex", flexDirection: "column", gap: 8 }}
              value={values.payment}
              onChange={(e) => setFieldValue("payment", e.target.value)}
            >
              <Radio value="inCash">{tf.inCash || "À vista"}</Radio>
              <Radio value="part" onClick={handleOpenCloseInstallmentsModal}>
                {tf.part || "Parcelado"}
              </Radio>
            </Radio.Group>
          </Row>
          <Row>
            <label>{tf.actions || "Ação"}</label>
          </Row>
          <Row>
            <Radio.Group
              style={{ display: "flex", flexDirection: "column", gap: 8 }}
              value={values.action}
              onChange={(e) => {
                setFieldValue("action", e.target.value);
                if (
                  e.target.value === "launchAndSettle" &&
                  values.expirationDate != null
                ) {
                  setFieldValue("settleDate", values.expirationDate);
                } else {
                  setFieldValue("settleDate", null);
                }
              }}
            >
              <Radio value="launch">
                {tf.actionsOptions?.launch || "Lançar"}
              </Radio>
              <Radio value="launchAndSettle">
                {tf.actionsOptions?.launchAndSettle || "Lançar e Baixar"}
              </Radio>
            </Radio.Group>
          </Row>
          {values.action === "launchAndSettle" && (
            <>
              <Row>
                <label className={hasError("settleDate") ? "label-error" : ""}>
                  {tf.settleDate || "Data da Baixa"}*
                </label>
              </Row>
              <Row>
                <DatePicker
                  style={{ width: "100%" }}
                  value={
                    values.settleDate
                      ? moment(values.settleDate).tz("America/Sao_Paulo")
                      : undefined
                  }
                  format="DD/MM/YYYY"
                  disabledDate={(current) =>
                    current.isAfter(momentTimeZoneZeroHour(moment()))
                  }
                  onChange={(date) =>
                    setFieldValue("settleDate", momentTimeZoneZeroHour(date))
                  }
                />
              </Row>
            </>
          )}
          {isEnablePaymentMethod && (
            <>
              <Row>
                <label>
                  {tf.financialPaymentMethodId || "Forma de Pagamento"}
                </label>
              </Row>
              <Row>
                <Select
                  allowClear
                  style={{ width: "100%" }}
                  loading={
                    financialPaymentMethod.loadingFinancialPaymentMethodDrowpdownData
                  }
                  value={values.financialPaymentMethodId || undefined}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.props.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  placeholder={translation.defaultSelectPlaceholder}
                  onChange={(value) =>
                    setFieldValue("financialPaymentMethodId", value)
                  }
                >
                  {(
                    financialPaymentMethod.financialPaymentMethodDropdownData ||
                    []
                  )
                    .filter((p) => p.status === "Active")
                    .map((p) => (
                      <Option key={p.id} value={p.id}>
                        {p.name}
                      </Option>
                    ))}
                </Select>
              </Row>
            </>
          )}
        </Col>

        {/* Natureza / Classe / Centro de Resultado */}
        <Col span={8}>
          <Row>
            <label
              className={hasError("financialNatureId") ? "label-error" : ""}
            >
              {tf.financialNatureId || "Natureza"}*
            </label>
          </Row>
          <Row>
            <Select
              style={{ width: "100%" }}
              value={
                isLoadingFinancialNature
                  ? undefined
                  : values.financialNatureId || undefined
              }
              loading={isLoadingFinancialNature}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.props.children
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
              placeholder={translation.defaultSelectPlaceholder}
              onChange={(value) => {
                setFieldValue("financialNatureId", value);
                setFieldValue("financialClassId", null);
              }}
            >
              {filteredListFinancialNature.map((l) => (
                <Option key={l.id} value={l.id}>
                  {l.name}
                </Option>
              ))}
            </Select>
          </Row>
          <Row>
            <label
              className={hasError("financialClassId") ? "label-error" : ""}
            >
              {tf.financialClassId || "Classe"}*
            </label>
          </Row>
          <Row>
            <Select
              style={{ width: "100%" }}
              value={
                isLoadingFinancialClass
                  ? undefined
                  : values.financialClassId || undefined
              }
              loading={isLoadingFinancialClass}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.props.children
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
              placeholder={translation.defaultSelectPlaceholder}
              onChange={(value) => setFieldValue("financialClassId", value)}
            >
              {values.financialNatureId != null &&
                listFinancialClass
                  .filter(
                    (l) => l.financialNature.id === values.financialNatureId,
                  )
                  .map((l) => (
                    <Option key={l.id} value={l.id}>
                      {l.name}
                    </Option>
                  ))}
            </Select>
          </Row>
          <Row>
            <label>{tf.resultCenter || "Centro de Resultado"}</label>
          </Row>
          <Row>
            <Radio.Group
              value={values.resultCenterApportionmentDefault}
              buttonStyle="solid"
              onChange={(e) =>
                setFieldValue(
                  "resultCenterApportionmentDefault",
                  e.target.value,
                )
              }
            >
              <Radio.Button value={true}>
                {tf.resultCenterApportionmentDefault || "Padrão"}
              </Radio.Button>
              <Radio.Button
                disabled={
                  values.financialNatureId == null || values.value == null
                }
                value={false}
                onClick={handleOpenCloseResultCenterModal}
              >
                {tf.resultCenterApportionmentCustom || "Redefinir"}
              </Radio.Button>
            </Radio.Group>
          </Row>
        </Col>
      </Row>

      <Row type="flex" gutter={16}>
        <Col span={8}>
          <Row>
            <label className={hasError("billingDate") ? "label-error" : ""}>
              {tf.billingDate || "Data do faturamento"}*
            </label>
          </Row>
          <Row>
            <DatePicker
              style={{ width: "100%" }}
              disabled
              value={
                values.billingDate
                  ? moment(values.billingDate).tz("America/Sao_Paulo")
                  : undefined
              }
              format="DD/MM/YYYY"
              onChange={(date) =>
                setFieldValue("billingDate", momentTimeZoneZeroHour(date))
              }
            />
          </Row>
        </Col>
        <Col span={8}>
          <Row>
            <label className={hasError("expirationDate") ? "label-error" : ""}>
              {tf.expirationDate || "Data do vencimento"}*
            </label>
          </Row>
          <Row>
            <DatePicker
              style={{ width: "100%" }}
              disabled={!values.billingDate}
              value={
                values.expirationDate
                  ? moment(values.expirationDate).tz("America/Sao_Paulo")
                  : undefined
              }
              format="DD/MM/YYYY"
              onChange={(date) => {
                setFieldValue("expirationDate", momentTimeZoneZeroHour(date));
                if (
                  values.action === "launchAndSettle" &&
                  values.settleDate == null
                ) {
                  setFieldValue("settleDate", momentTimeZoneZeroHour(date));
                }
              }}
            />
          </Row>
        </Col>
        <Col span={8}>
          <Row>
            <label>{tf.financialProjectId || "Projeto Financeiro"}</label>
          </Row>
          <Row>
            <Select
              allowClear
              style={{ width: "100%" }}
              value={values.financialProjectId || undefined}
              loading={isLoadingFinancialProjects}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.props.children
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
              placeholder={translation.defaultSelectPlaceholder}
              onDeselect={() => setFieldValue("financialProjectId", null)}
              onChange={(value) => setFieldValue("financialProjectId", value)}
            >
              {listFinancialProjects.map((l) => (
                <Option key={l.id} value={l.id}>
                  {l.name}
                </Option>
              ))}
            </Select>
          </Row>
        </Col>
      </Row>

      {(isEnableFinancialTaxableDeductible ||
        isEnableFinancialIssue ||
        isEnableBankAccountFarm) && (
        <Row gutter={16}>
          {isEnableFinancialTaxableDeductible && (
            <Col span={8}>
              <Row>
                <label>
                  {tf.financialTaxableDeductibleId || "Tributável/Dedutivel IR"}
                </label>
              </Row>
              <Row>
                <Select
                  allowClear
                  style={{ width: "100%" }}
                  loading={
                    financialTaxableDeductible.loadingFinancialTaxableDeductibleDrowpdownData
                  }
                  value={values.financialTaxableDeductibleId || undefined}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.props.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  placeholder={translation.defaultSelectPlaceholder}
                  onChange={(value) =>
                    setFieldValue("financialTaxableDeductibleId", value)
                  }
                >
                  {(
                    financialTaxableDeductible.financialTaxableDeductibleDrowpdownData ||
                    []
                  )
                    .filter((t) => t.status === "Active")
                    .map((t) => (
                      <Option key={t.id} value={t.id}>
                        {t.name}
                      </Option>
                    ))}
                </Select>
              </Row>
            </Col>
          )}
          {isEnableFinancialIssue && (
            <Col span={8}>
              <Row>
                <label>{tf.financialIssueId || "Pendência Financeira"}</label>
              </Row>
              <Row>
                <Select
                  allowClear
                  style={{ width: "100%" }}
                  loading={financialIssue.loadingFinancialIssueDropdownData}
                  value={values.financialIssueId || undefined}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.props.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  placeholder={translation.defaultSelectPlaceholder}
                  onChange={(value) => setFieldValue("financialIssueId", value)}
                >
                  {(financialIssue.financialIssueDropdownData || [])
                    .filter((i) => i.status === "Active")
                    .map((i) => (
                      <Option key={i.id} value={i.id}>
                        {i.name}
                      </Option>
                    ))}
                </Select>
              </Row>
            </Col>
          )}
          {isEnableBankAccountFarm && (
            <Col span={8}>
              <Row>
                <label
                  className={hasError("bankAccountFarmId") ? "label-error" : ""}
                >
                  {`${tf.bankAccountFarmId}*` || "Fonte Pagadora*"}
                </label>
              </Row>
              <Row>
                <Select
                  style={{ width: "100%" }}
                  value={values.bankAccountFarmId || undefined}
                  loading={isLoadingBankAccountFarm}
                  placeholder={translation.defaultSelectPlaceholder}
                  onChange={(value) =>
                    setFieldValue("bankAccountFarmId", value)
                  }
                >
                  {bankAccountFarmOptions
                    .filter((b) => b.status === "Active")
                    .map((b) => (
                      <Option key={b.id} value={b.id}>
                        {b.description}
                      </Option>
                    ))}
                </Select>
              </Row>
            </Col>
          )}
        </Row>
      )}

      <Row gutter={16}>
        <Col span={24}>
          <Row>
            <label>{tf.description || "Descrição"}</label>
          </Row>
          <Row>
            <TextArea
              autoSize={{ minRows: 3 }}
              value={values.description || ""}
              placeholder={translation.defaultPlaceholder}
              onChange={(e) => setFieldValue("description", e.target.value)}
            />
          </Row>
        </Col>
      </Row>

      <InstallmentsModal
        isInstallmentsModalVisible={isInstallmentsModalVisible}
        closeInstallmentsModal={handleOpenCloseInstallmentsModal}
        installments={values.installments}
        installmentTotal={values.installmentTotal}
        expirationDate={values.expirationDate}
        value={values.value}
        valueCurrencySymbol={defaultCurrency?.symbol ?? "R$"}
        setFieldValue={setFieldValue}
      />
      <ResultCenterApportionmentModal
        isVisible={isResultCenterModalVisible}
        closeModal={handleCancelCustomApportionmentModal}
        saveModal={handleSaveCustomApportionmentModal}
        financialNatureId={values.financialNatureId}
        transactionValue={values.value}
        valueCurrency={defaultCurrencyCode ?? "BRL"}
      />
    </FinancialTransactionBlock>
  );
};

export default FinancialTransactionBox;
